/**
 * Dashboard card copy helpers (R3b). Keep in sync with src/utils/dashboard-copy.js.
 * Frontend cannot import from src/, so the rules live here for the v2 UI.
 */

export function stripMarkdownLite(text) {
  return String(text || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/^[ \t]*#{1,6}\s+/gm, '')
    .replace(/^[ \t]*[-*+]\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^[ \t]*>\s?/gm, '')
    .replace(/[#>*_|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function clipAtWord(text, max) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  const limit = Math.max(1, Number(max) || 1);
  if (clean.length <= limit) return clean;
  const cut = clean.slice(0, Math.max(1, limit - 1));
  const sp = cut.lastIndexOf(' ');
  const body = (sp > Math.floor(limit * 0.45) ? cut.slice(0, sp) : cut).trim();
  return `${body}…`;
}

export function firstSentence(text, max) {
  const line = String(text || '').replace(/\s+/g, ' ').trim().split(/\n/)[0] || '';
  if (!line) return '';
  const match = line.match(/^(.+?[.!?])(\s|$)/);
  const title = (match && match[1].length >= 12 && match[1].length <= 80) ? match[1] : line;
  return clipAtWord(title, max);
}

export function insightCardPreview(card, opts) {
  if (card && card.card_title) {
    return {
      title: String(card.card_title),
      summary: String(card.card_summary || ''),
    };
  }
  const titleMax = (opts && opts.titleMax) || 90;
  const lineMax = (opts && opts.lineMax) || 140;
  const plainHead = stripMarkdownLite(card && card.headline);
  const plainBody = stripMarkdownLite(card && card.body);
  const title = clipAtWord(plainHead || plainBody, titleMax) || 'Insight';
  const sentences = plainBody
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((s) => s.toLowerCase() !== title.toLowerCase());
  const pool = sentences.length ? sentences : (plainBody ? [plainBody] : []);
  const summary = pool.slice(0, 2).map((s) => clipAtWord(s, lineMax)).filter(Boolean).join('\n');
  return { title, summary };
}

export function scheduleDisplayTitle(row, max = 60) {
  const named = String((row && (row.name || row.title)) || '').trim();
  if (named) return clipAtWord(named, max);
  return firstSentence((row && row.prompt) || '', max) || 'Untitled schedule';
}

export function scheduleCardMeta(row, formatRelative) {
  const cadence = String((row && row.cadence) || '').trim();
  const cadenceLabel = cadence ? cadence.charAt(0).toUpperCase() + cadence.slice(1) : '';
  const parts = [];
  if (cadenceLabel) parts.push(cadenceLabel);
  if (row && row.next_run_at && formatRelative) {
    parts.push(`Next ${formatRelative(row.next_run_at)}`);
  }
  if (row && row.enabled === false) {
    parts.push('Paused');
  } else if (row && row.last_error) {
    const err = String(row.last_error).replace(/^Skipped:\s*/i, '').trim();
    parts.push(err ? `Last failed: ${clipAtWord(err, 48)}` : 'Last run failed');
  } else if (row && row.last_run_at && formatRelative) {
    parts.push(`Last ${formatRelative(row.last_run_at)}`);
  }
  return parts.join(' · ') || 'Scheduled';
}

const KPI_CONNECT = {
  mau: { source: 'Google Analytics or Firebase', metric: 'Monthly actives' },
  act: { source: 'Google Analytics or Firebase', metric: 'Activation rate' },
  d7: { source: 'Google Analytics or Firebase', metric: 'D7 retention' },
  paid: { source: 'RevenueCat', metric: 'Trial → paid' },
};

export function kpiConnectHint(id) {
  const hit = KPI_CONNECT[id];
  if (!hit) {
    return { message: 'Connect a source to see this metric' };
  }
  return {
    source: hit.source,
    metric: hit.metric,
    message: `Connect ${hit.source} to see ${hit.metric}`,
  };
}

export function sanitizeKpiTrend(trend) {
  const raw = String(trend || '').trim();
  const lower = raw.toLowerCase();
  if (!raw || lower === 'no reading yet' || lower === 'first reading') return '';
  return raw;
}

export function kpiHasReading(kpi) {
  if (!kpi) return false;
  if (kpi.available === false) return false;
  const value = kpi.value;
  if (value == null || value === '' || value === '—') return false;
  return true;
}
